// Zod schemas for REST API v1 inputs and outputs. Shared by auth-server (validation + OpenAPI) and
// console (typed client). Wire format: JSON, camelCase, ISO 8601 UTC strings for dates.
import { z } from "zod";
import { PERMISSIONS } from "./permissions";
import { EVENT_TYPES } from "./events";
import { MAX_PAGE_SIZE } from "./cursor";
import { IDENTIFIER_TYPES } from "./identifiers";

const isoDate = z.iso.datetime({ offset: true });

export const UserStatusSchema = z.enum(["active", "banned", "pending"]);
export const PermissionSchema = z.enum(PERMISSIONS);
export const LocaleSchema = z.enum(["th", "en"]);

const emailSchema = z.email().max(254);
const displayNameSchema = z.string().trim().min(1).max(200);
const roleIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_-]{1,40}$/, "lowercase letters, digits, - or _ (2–41 chars)");
/** Ids of user types and groups: same shape as role ids (readable in tokens). */
const slugSchema = roleIdSchema;
const httpsOrLocalUrl = z.url().refine(
  (u) => {
    try {
      const url = new URL(u);
      if (url.hash) return false;
      if (url.protocol === "https:") return true;
      // http only for the loopback: localhost, *.localhost (RFC 6761 — always resolves to the loopback,
      // e.g. one local app per sub-domain like ttw.pacgold.localhost), 127.0.0.1 and [::1]
      if (url.protocol === "http:")
        return (
          ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname) || url.hostname.endsWith(".localhost")
        );
      // custom schemes for native apps (e.g. com.example.app:/callback)
      return /^[a-z][a-z0-9+.-]*\.[a-z0-9+.-]+:$/.test(url.protocol);
    } catch {
      return false;
    }
  },
  { message: "Must be https (http allowed only for localhost / *.localhost), no fragment" },
);

export const CustomFieldValueSchema = z.union([z.string().max(1000), z.number(), z.boolean(), z.null()]);

// ---------- Users ----------
export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  displayName: z.string(),
  avatarUrl: z.string().optional(),
  status: UserStatusSchema,
  banReason: z.string().optional(),
  roles: z.array(z.string()),
  mfaEnabled: z.boolean(),
  linkedProviders: z.array(z.string()),
  userCode: z
    .string()
    .optional()
    .describe("User code (รหัสผู้ใช้งาน, identifier type `user_code`), e.g. `root`"),
  username: z.string().optional().describe("Username (ชื่อผู้ใช้, identifier type `username`)"),
  userType: z
    .string()
    .nullable()
    .describe(
      "User type id (`employee`, `customer`, `partner` or a custom type); `null` only for the root account " +
        "(system account: no type, console only)",
    ),
  groups: z.array(z.string()).describe("Group ids"),
  createdAt: isoDate,
  lastSignInAt: isoDate.optional(),
});
export type UserDto = z.infer<typeof UserSchema>;

export const EmployeeProfileSchema = z.object({
  userId: z.string(),
  employeeCode: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  department: z.string().optional(),
  position: z.string().optional(),
  custom: z.record(z.string(), CustomFieldValueSchema),
  updatedAt: isoDate,
});
export type EmployeeProfileDto = z.infer<typeof EmployeeProfileSchema>;

export const UserDetailSchema = UserSchema.extend({ profile: EmployeeProfileSchema.nullable() });
export type UserDetailDto = z.infer<typeof UserDetailSchema>;

export const ListUsersQuerySchema = z.object({
  q: z
    .string()
    .trim()
    .max(200)
    .optional()
    .describe(
      "Search: email, additional emails / phones / user code / username, name, user id, employee code, department",
    ),
  status: UserStatusSchema.optional(),
  role: z.string().max(50).optional().describe("Role id"),
  mfa: z.enum(["true", "false"]).optional(),
  type: z.string().max(50).optional().describe("User type id"),
  group: z.string().max(50).optional().describe("Group id"),
  sort: z
    .enum(["createdAt", "email", "displayName"])
    .optional()
    .describe("Sort field (default `createdAt`, newest first)"),
  order: z
    .enum(["asc", "desc"])
    .optional()
    .describe("Sort direction (default `desc` for `createdAt`, `asc` otherwise)"),
  cursor: z.string().max(500).optional().describe("Opaque cursor from `nextCursor` of the previous page"),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional().describe("Page size (default 25)"),
});

export const CreateUserBodySchema = z.object({
  email: emailSchema,
  displayName: displayNameSchema,
  password: z.string().min(8).max(256).optional(),
  roles: z.array(roleIdSchema).max(20).optional(),
  emailVerified: z.boolean().optional(),
  sendInvite: z.boolean().default(true),
  userType: slugSchema.optional().describe("Default `employee`"),
  groups: z.array(slugSchema).max(50).optional(),
  userCode: z
    .string()
    .trim()
    .max(64)
    .optional()
    .describe(
      "User code (รหัสผู้ใช้งาน, identifier type `user_code`): another sign-in identifier; must be unused",
    ),
  username: z
    .string()
    .trim()
    .max(64)
    .optional()
    .describe(
      "Username (ชื่อผู้ใช้, identifier type `username`): another sign-in identifier, same format as a user code; " +
        "must be unused as a user code or username",
    ),
});
export type CreateUserBody = z.infer<typeof CreateUserBodySchema>;

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

export const ProfilePatchSchema = z.object({
  employeeCode: optionalText(50),
  firstName: optionalText(100),
  lastName: optionalText(100),
  department: optionalText(100),
  position: optionalText(100),
  custom: z.record(z.string(), CustomFieldValueSchema).optional(),
});

export const UpdateUserBodySchema = z.object({
  displayName: displayNameSchema.optional(),
  avatarUrl: z.url().max(2000).nullable().optional(),
  email: emailSchema.optional(),
  emailVerified: z.boolean().optional(),
  userType: slugSchema.optional().describe("Change the user type (revokes the user's application tokens)"),
  profile: ProfilePatchSchema.optional(),
});
export type UpdateUserBody = z.infer<typeof UpdateUserBodySchema>;

// ---------- Sign-in identifiers (additional emails / phone numbers) ----------
export const IdentifierTypeSchema = z.enum(IDENTIFIER_TYPES);

export const UserIdentifierSchema = z.object({
  /** `primary-email` for the engine's primary email (not stored in the identifier table). */
  id: z.string(),
  type: IdentifierTypeSchema,
  value: z.string(),
  verified: z.boolean(),
  primary: z.boolean(),
  createdAt: isoDate,
  verifiedAt: isoDate.optional(),
});
export type UserIdentifierDto = z.infer<typeof UserIdentifierSchema>;

export const IdentifierListSchema = z.object({
  items: z.array(UserIdentifierSchema),
  /** Self-service: whether add / remove / make-primary must include the current password. */
  passwordRequired: z.boolean().optional(),
  /** Whether phone numbers can be verified by SMS on this server. */
  smsAvailable: z.boolean(),
});

export const AddIdentifierBodySchema = z.object({
  type: IdentifierTypeSchema,
  /** Email address or phone number (national numbers use the server's default country code). */
  value: z.string().trim().min(3).max(254),
  /** Admin only: mark as verified without sending a code. */
  verified: z.boolean().optional(),
});
export type AddIdentifierBody = z.infer<typeof AddIdentifierBodySchema>;

/** Self-service changes re-confirm the password (when the account has one): a stolen session alone
 * must not be able to attach the attacker's phone / email to the account. */
const passwordConfirm = { password: z.string().max(256).optional() };
export const MeAddIdentifierBodySchema = AddIdentifierBodySchema.omit({ verified: true }).extend(
  passwordConfirm,
);
export const PasswordConfirmBodySchema = z.object(passwordConfirm);

export const VerifyIdentifierBodySchema = z.object({ code: z.string().regex(/^\d{6}$/, "6 digits") });

export const BanBodySchema = z.object({ reason: z.string().trim().min(1).max(500) });
export const SetRolesBodySchema = z.object({ roles: z.array(roleIdSchema).max(20) });

export const SessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  createdAt: isoDate,
  expiresAt: isoDate,
  ip: z.string().optional(),
  userAgent: z.string().optional(),
  current: z.boolean().optional(),
});
export type SessionDto = z.infer<typeof SessionSchema>;

export const MeSchema = z.object({
  user: UserSchema,
  roles: z.array(z.string()),
  permissions: z.array(PermissionSchema),
  mfaRequired: z.boolean(),
  consoleAccess: z.boolean(),
  /** Root still signs in with its generated initial password (readable with `cli root:initial-password`). */
  usingInitialPassword: z.boolean().optional(),
  /** An admin set the password: the user must choose a new one before using any application. */
  passwordChangeRequired: z.boolean().optional(),
});
export type MeDto = z.infer<typeof MeSchema>;

// ---------- Roles ----------
export const RoleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  permissions: z.array(PermissionSchema),
  builtIn: z.boolean(),
});
export type RoleDto = z.infer<typeof RoleSchema>;

export const CreateRoleBodySchema = z.object({
  name: roleIdSchema,
  description: z.string().trim().max(500).optional(),
  permissions: z.array(PermissionSchema),
});
export const UpdateRoleBodySchema = CreateRoleBodySchema.partial();

const domainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(\*\.)?([a-z0-9-]+\.)+[a-z]{2,}$/, "Invalid domain");

// ---------- User types, groups, application access (docs/access.md) ----------
export const UserTypePolicySchema = z.object({
  selfSignUp: z.boolean().describe("Visitors may sign up as this type through an application that offers it"),
  emailDomains: z
    .array(domainSchema)
    .max(50)
    .describe("Self sign-ups with these email domains get this type (`*.example.com` for sub-domains)"),
  mfa: z.enum(["inherit", "optional", "required"]).describe("2FA rule; `inherit` = global MFA policy"),
  defaultGroups: z.array(slugSchema).max(50).describe("Groups a new account of this type joins"),
});

export const UserTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  builtIn: z.boolean(),
  policy: UserTypePolicySchema,
  userCount: z.number().int().describe("Accounts of this type"),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type UserTypeDto = z.infer<typeof UserTypeSchema>;

export const CreateUserTypeBodySchema = z.object({
  id: slugSchema.describe("Immutable id; appears in tokens as `user_type`"),
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional(),
  policy: UserTypePolicySchema.partial().optional(),
});
export const UpdateUserTypeBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  policy: UserTypePolicySchema.partial().optional(),
});

export const GroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  memberCount: z.number().int(),
  createdAt: isoDate,
  updatedAt: isoDate,
  createdBy: z.string().optional(),
});
export type GroupDto = z.infer<typeof GroupSchema>;

export const CreateGroupBodySchema = z.object({
  id: slugSchema.describe("Immutable id; appears in tokens in the `groups` claim"),
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional(),
});
export const UpdateGroupBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().trim().max(500).nullable().optional(),
});
export const GroupMembersBodySchema = z.object({
  userIds: z.array(z.string().min(1).max(64)).min(1).max(500),
});
export const GroupMembersSchema = z.object({ items: z.array(UserSchema) });
export const SetUserGroupsBodySchema = z.object({ groups: z.array(slugSchema).max(50) });

export const ClientAccessSchema = z
  .object({
    mode: z
      .enum(["all", "restricted"])
      .describe("`all`: every account · `restricted`: listed types / groups"),
    userTypes: z.array(slugSchema).max(50).default([]),
    groups: z.array(slugSchema).max(100).default([]),
    signUpUserType: slugSchema
      .optional()
      .describe("User type given to accounts that sign up through this application (absent = no sign-up)"),
  })
  .refine((a) => a.mode === "all" || a.userTypes.length + a.groups.length > 0, {
    message: "A restricted application needs at least one user type or group",
    path: ["userTypes"],
  });
export type ClientAccessDto = z.infer<typeof ClientAccessSchema>;

// ---------- Admin password set ----------
export const SetUserPasswordBodySchema = z
  .object({
    password: z
      .string()
      .min(1)
      .max(256)
      .optional()
      .describe("New password; omit to let the server generate one (returned once in the response)"),
  })
  .meta({ id: "SetUserPassword" });
export const SetUserPasswordResultSchema = z
  .object({
    password: z.string().optional().describe("The generated password — present only when none was sent"),
    passwordChangeRequired: z.literal(true),
  })
  .meta({ id: "SetUserPasswordResult" });

// ---------- OAuth clients ----------
const localizedText = (max: number) =>
  z
    .object({ th: z.string().trim().max(max).optional(), en: z.string().trim().max(max).optional() })
    .meta({ description: "Per UI language; empty / missing = built-in text" });
export const HeroTitleSchema = localizedText(120).describe(
  "Sign-in brand panel headline; line breaks are kept",
);
export const HeroBodySchema = localizedText(500).describe("Text under the headline");

export const ClientBrandingSchema = z
  .object({
    logoUrl: httpsOrLocalUrl.max(2000).optional().describe("Logo on this application's sign-in pages"),
    primaryColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .optional()
      .describe("Brand color `#rrggbb`; text colors are derived to keep WCAG AA contrast"),
    heroTitle: HeroTitleSchema.optional(),
    heroBody: HeroBodySchema.optional(),
  })
  .meta({ id: "ClientBranding" });
export type ClientBrandingDto = z.infer<typeof ClientBrandingSchema>;

export const OIDC_SCOPES = [
  "openid",
  "profile",
  "email",
  "phone",
  "identities",
  "offline_access",
  "roles",
  "groups",
  "employee",
] as const;
export const ScopeSchema = z.enum(OIDC_SCOPES);

export const OAuthClientSchema = z.object({
  clientId: z.string(),
  name: z.string(),
  type: z.enum(["confidential", "public"]),
  redirectUris: z.array(z.string()),
  postLogoutRedirectUris: z.array(z.string()),
  allowedScopes: z.array(z.string()),
  firstParty: z.boolean(),
  backchannelLogoutUri: z.string().optional(),
  disabled: z.boolean(),
  ownerTeam: z.string().optional(),
  description: z.string().optional(),
  access: ClientAccessSchema,
  branding: ClientBrandingSchema.optional().describe("Absent = global branding"),
  createdAt: isoDate,
});
export type OAuthClientDto = z.infer<typeof OAuthClientSchema>;

export const RegisterClientBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
  type: z.enum(["confidential", "public"]),
  redirectUris: z.array(httpsOrLocalUrl).min(1).max(20),
  postLogoutRedirectUris: z.array(httpsOrLocalUrl).max(20).default([]),
  allowedScopes: z.array(ScopeSchema).default(["openid", "profile", "email"]),
  firstParty: z.boolean().default(false),
  backchannelLogoutUri: httpsOrLocalUrl.optional(),
  ownerTeam: z.string().trim().max(100).optional(),
  description: z.string().trim().max(500).optional(),
  access: ClientAccessSchema.optional().describe("Default: employees only"),
  branding: ClientBrandingSchema.optional(),
});
export type RegisterClientBody = z.infer<typeof RegisterClientBodySchema>;

export const UpdateClientBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  redirectUris: z.array(httpsOrLocalUrl).min(1).max(20).optional(),
  postLogoutRedirectUris: z.array(httpsOrLocalUrl).max(20).optional(),
  allowedScopes: z.array(ScopeSchema).optional(),
  firstParty: z.boolean().optional(),
  backchannelLogoutUri: httpsOrLocalUrl.nullable().optional(),
  ownerTeam: z.string().trim().max(100).nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  access: ClientAccessSchema.optional(),
  branding: ClientBrandingSchema.nullable().optional().describe("null = use the global branding"),
});

export const RegisterClientResultSchema = z.object({
  client: OAuthClientSchema,
  clientSecret: z.string().optional(),
});
export const RotateSecretResultSchema = z.object({ clientSecret: z.string() });

// ---------- Settings ----------
export const CustomFieldDefinitionSchema = z
  .object({
    key: z.string().regex(/^[a-z][a-zA-Z0-9_]{1,40}$/),
    label: z.object({ th: z.string().trim().min(1).max(100), en: z.string().trim().min(1).max(100) }),
    type: z.enum(["string", "number", "boolean", "date", "select"]),
    options: z.array(z.string().trim().min(1).max(100)).max(100).optional(),
    required: z.boolean(),
    showOnSignUp: z.boolean(),
    includeInClaims: z.boolean(),
  })
  .refine((d) => d.type !== "select" || (d.options?.length ?? 0) > 0, {
    message: "select fields need options",
    path: ["options"],
  });

export const CustomFieldsSettingSchema = z
  .array(CustomFieldDefinitionSchema)
  .max(50)
  .refine((arr) => new Set(arr.map((f) => f.key)).size === arr.length, { message: "Duplicate field keys" });

export const LoginMethodConfigSchema = z.object({
  emailPassword: z.object({
    enabled: z.boolean(),
    allowSignUp: z.boolean(),
    requireEmailVerification: z.boolean(),
    allowedEmailDomains: z.array(domainSchema).max(50),
    minPasswordLength: z.number().int().min(8).max(128),
  }),
  social: z.record(z.string().regex(/^[a-z][a-z0-9-]{1,30}$/), z.object({ enabled: z.boolean() })),
  passkey: z.object({ enabled: z.boolean() }),
  mfa: z.object({
    policy: z.enum(["optional", "required_for_admins", "required_for_all"]),
    methods: z.array(z.enum(["totp", "email_otp"])).min(1),
  }),
  session: z.object({
    maxAgeDays: z.number().int().min(1).max(365),
    idleTimeoutMinutes: z
      .number()
      .int()
      .min(5)
      .max(60 * 24 * 30)
      .optional(),
  }),
  phone: z.object({ enabled: z.boolean(), otpSignIn: z.boolean() }).optional(),
});

export const BrandingSchema = z.object({
  productName: z.string().trim().min(1).max(60),
  logoUrl: z.url().max(2000).optional(),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  supportEmail: z.email().optional(),
  heroTitle: HeroTitleSchema.optional(),
  heroBody: HeroBodySchema.optional(),
});

export const LogRetentionSchema = z.object({ days: z.number().int().min(1).max(3650) });

export const SETTING_SCHEMAS = {
  "login-methods": LoginMethodConfigSchema,
  "custom-fields": CustomFieldsSettingSchema,
  branding: BrandingSchema,
  "log-retention": LogRetentionSchema,
} as const;

export const SettingEnvelopeSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  version: z.number().int(),
  updatedAt: isoDate,
  updatedBy: z.string().optional(),
});

export const ProviderStatusSchema = z.object({
  id: z.string(),
  configured: z.boolean(),
  supported: z.boolean(),
});

export const PublicLoginMethodsSchema = z.object({
  emailPassword: z.object({
    enabled: z.boolean(),
    allowSignUp: z.boolean(),
    /** A user type maps email domains (company addresses can sign up without an application offering it). */
    signUpByEmailDomain: z.boolean(),
    minPasswordLength: z.number(),
    domainsHint: z.array(z.string()),
  }),
  social: z.array(z.object({ id: z.string(), name: z.string() })),
  passkey: z.boolean(),
  /** Phone numbers accepted in the sign-in field; `otpSignIn` only when an SMS provider is configured. */
  phone: z.object({ enabled: z.boolean(), otpSignIn: z.boolean() }),
  mfaMethods: z.array(z.string()),
  signUpFields: z.array(CustomFieldDefinitionSchema),
});
export type PublicLoginMethods = z.infer<typeof PublicLoginMethodsSchema>;

// ---------- Audit logs ----------
export const EventTypeSchema = z.enum(EVENT_TYPES);
export const AuditLogEntrySchema = z.object({
  id: z.string(),
  type: z.string(),
  actor: z.object({
    type: z.enum(["user", "system"]),
    id: z.string(),
    ip: z.string().optional(),
    userAgent: z.string().optional(),
  }),
  subjectUserId: z.string().optional(),
  data: z.record(z.string(), z.unknown()),
  occurredAt: isoDate,
});
export type AuditLogEntryDto = z.infer<typeof AuditLogEntrySchema>;

export const ListAuditLogsQuerySchema = z.object({
  type: z.union([z.string(), z.array(z.string())]).optional(),
  actor: z.string().max(100).optional(),
  subject: z.string().max(100).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional(),
});

// ---------- Stats ----------
export const OverviewStatsSchema = z.object({
  totalUsers: z.number(),
  active24h: z.number(),
  active7d: z.number(),
  signInsToday: z.number(),
  signInsPerDay: z.array(z.object({ date: z.string(), count: z.number() })),
  failedSignIns24h: z.number(),
  mfaAdoptionPercent: z.number(),
});

// ---------- Export / import ----------
export const ExportJobSchema = z.object({
  id: z.string(),
  status: z.enum(["pending", "running", "done", "failed"]),
  scope: z.string(),
  requestedBy: z.string(),
  fileRef: z.string().optional(),
  createdAt: isoDate,
  finishedAt: isoDate.optional(),
  error: z.string().optional(),
});
export type ExportJobDto = z.infer<typeof ExportJobSchema>;
export const CreateExportBodySchema = z.object({
  includeAuditLogs: z.boolean().default(false),
  includeCredentials: z.boolean().default(false),
});
export const ImportReportSchema = z.object({
  dryRun: z.boolean(),
  counts: z.record(z.string(), z.number()),
  skipped: z.number(),
  errors: z.array(z.object({ line: z.number(), message: z.string() })),
});

// ---------- Problem ----------
export const ProblemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number(),
  detail: z.string().optional(),
  code: z.string(),
  errors: z
    .array(z.object({ path: z.string(), message: z.string(), code: z.string().optional() }))
    .optional(),
  instance: z.string().optional(),
});

export function pageSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().optional().describe("Cursor of the next page; absent on the last page"),
    /** Total matching items (when available). */
    total: z.number().int().optional().describe("Total number of matching items (when available)"),
  });
}
